import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { About } from './About'
import { SectionTitles } from './SectionTitles'
import { ProjectInfo } from './ProjectInfo'
import { AudioButton } from './AudioButton'
import gnsLogo from '/Logo/gnscube_logo_transparnt.png'
import styled from 'styled-components'
import { useSectionsStore } from './store'

// Import logo SVGs
import token2049Logo from '/Logo/token 2049.svg'
import artBaselLogo from '/Logo/art basel.svg'
import nftNycLogo from '/Logo/NFT nyc.svg'
import nfcSummitLogo from '/Logo/NFC SUMMIT.svg'
import vuseLogo from '/Logo/vuse-logo.png'

// Simple arrow component to replace Material-UI icon
const ArrowIcon = ({ style }) => (
  <span style={{ ...style, fontSize: '1.8em' }}>→</span>
);

// Animated link component with hover effect
const AnimatedLink = ({ href, children, style, icon = false, onClick = null }) => {
  return (
    <motion.a
      href={onClick ? undefined : href}
      onClick={onClick}
      target={onClick ? undefined : "_blank"}
      rel={onClick ? undefined : "noopener noreferrer"}
      initial={{ scale: 1 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 17
      }}
      style={{
        ...style,
        color: 'white',
        textDecoration: 'none',
        pointerEvents: 'all',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer'
      }}
    >
      {children}
      {icon && (
        <motion.div
          initial={{ x: 0 }}
          whileHover={{ x: 5 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <ArrowIcon style={{ color: '#64ffda' }} />
        </motion.div>
      )}
    </motion.a>
  );
};

// TypewriterText component with both typing and scrambling effects
const TypewriterText = ({ text, visible, delay = 0, duration = 1, useAsciiEffect = false, customStyle = {}, segments = null, useTypingEffect = false, useFadeIn = false }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isAnimating, setIsAnimating] = useState(false);
  const [opacity, setOpacity] = useState(useFadeIn ? 0 : 1);
  
  // Reduced character set for smoother scrambling
  const randomChars = 'ABCDEFGHIJKPQRSTUVWXYZ06789!#$%&*___♬҂彡益ヅ言㋛ツ۝ꈊ⽘✪ⓧ★ᐛಢ艸웃❣';
  
  useEffect(() => {
    if (!visible) {
      setDisplayedText("");
      setIsAnimating(false);
      setOpacity(useFadeIn ? 0 : 1);
      return;
    }
    
    let animationFrame;
    let startTime;
    
    // Start animation after delay
    const startTimer = setTimeout(() => {
      setIsAnimating(true);
      startTime = Date.now();
      
      if (useTypingEffect) {
        // Enhanced typing effect with scrambled character at cursor
        const typeText = () => {
          const elapsed = Date.now() - startTime;
          
          // Variable typing speed - fast initially, then slower
          const progress = elapsed / 1200; // Reduced from 2000 to 1200ms (1.2 seconds total)
          const baseSpeed = 9; // Reduced from 15 to 8ms (much faster initial speed)
          const slowSpeed = 20; // Reduced from 40 to 25ms (faster final speed)
          const currentSpeed = baseSpeed + (slowSpeed - baseSpeed) * Math.min(progress, 1);
          
          const charactersToShow = Math.floor(elapsed / currentSpeed);
          
          if (charactersToShow >= text.length) {
            setDisplayedText(text);
            setIsAnimating(false);
            return;
          }
          
          // Show typed text + scrambled character at cursor position
          let currentText = text.substring(0, charactersToShow);
          
          // Add scrambled character at the current position if not at end
          if (charactersToShow < text.length) {
            const nextChar = text[charactersToShow];
            if (nextChar !== ' ') {
              // Scramble the next character
              const scrambleIndex = Math.floor((elapsed * 0.015) % randomChars.length); // Faster scrambling
              currentText += randomChars[scrambleIndex];
            } else {
              currentText += ' '; // Keep spaces as spaces
            }
          }
          
          setDisplayedText(currentText);
          animationFrame = requestAnimationFrame(typeText);
        };
        
        typeText();
      } else {
        // Enhanced scrambling effect with fade-in and sine wave
        const animate = () => {
          const elapsed = Date.now() - startTime;
          const totalDuration = 2500; // Reduced from 3500 to 2500 (1 second shorter)
          const progress = Math.min(elapsed / totalDuration, 1);
          
          // Smooth fade-in over first 800ms
          if (useFadeIn) {
            const fadeProgress = Math.min(elapsed / 800, 1);
            setOpacity(fadeProgress);
          }
          
          if (progress >= 1) {
            // Animation complete - show final text
            setDisplayedText(text);
            setIsAnimating(false);
            setOpacity(1);
            return;
          }
          
          let currentText = '';
          
          // Progressive scramble attempts: 4 -> 3 -> 2
          const maxAttempts = progress < 0.3 ? 4 : progress < 0.6 ? 3 : 2;
          
          for (let i = 0; i < text.length; i++) {
            const charProgress = Math.max(0, (progress * text.length - i) / 3);
            
            if (text[i] === ' ') {
              currentText += ' ';
            } else if (charProgress >= 0.9) {
              // Character locks in during final 10% of its animation
              currentText += text[i];
            } else if (charProgress > 0) {
              // Enhanced scrambling with sine wave patterns
              const charOffset = i * 0.7 + elapsed * 0.001;
              const scramblePhase = Math.floor(charOffset) % maxAttempts;
              
              // Sine wave-based scrambling pattern for smoother effect
              const sineWave1 = Math.sin((elapsed * 0.003) + (i * 0.4)) * 0.5 + 0.5;
              const sineWave2 = Math.sin((elapsed * 0.002) + (i * 0.6) + Math.PI/3) * 0.5 + 0.5;
              const combinedWave = (sineWave1 + sineWave2) / 2;
              
              // Smooth sparse pattern using sine waves
              const shouldScramble = combinedWave > 0.6 && (i + Math.floor(elapsed * 0.002)) % 4 < 2;
              
              // Progressive reveal with sine wave influence
              const baseRevealChance = charProgress * 0.8 + (progress * 0.3);
              const sineInfluence = Math.sin(charProgress * Math.PI) * 0.2;
              const revealChance = baseRevealChance + sineInfluence;
              const showReal = Math.random() < revealChance;
              
              if (showReal || !shouldScramble) {
                // Show real character
                currentText += text[i];
              } else {
                // Show scrambled character with sine wave selection
                const scrambledChars = [
                  randomChars[(text[i].charCodeAt(0) + 3) % randomChars.length],
                  randomChars[(text[i].charCodeAt(0) + 11) % randomChars.length],
                  randomChars[(text[i].charCodeAt(0) + 19) % randomChars.length],
                  randomChars[(text[i].charCodeAt(0) + 27) % randomChars.length]
                ];
                const sineIndex = Math.floor(combinedWave * scrambledChars.length);
                currentText += scrambledChars[sineIndex];
              }
            } else {
              // Character hasn't started revealing yet - smooth initial state
              const initialSine = Math.sin((elapsed * 0.001) + (i * 0.3)) * 0.5 + 0.5;
              const shouldShowScrambled = initialSine > 0.7;
              if (shouldShowScrambled) {
                const staticOffset = (i * 5 + Math.floor(elapsed * 0.001)) % randomChars.length;
                currentText += randomChars[staticOffset];
              } else {
                currentText += text[i];
              }
            }
          }
          
          setDisplayedText(currentText);
          animationFrame = requestAnimationFrame(animate);
        };
        
        animate();
      }
    }, delay * 500);
    
    return () => {
      clearTimeout(startTimer);
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [text, visible, delay, useTypingEffect, useFadeIn]);
  
  // Function to render text with styled segments
  const renderTextWithStyles = () => {
    if (!segments || segments.length === 0) {
      return displayedText;
    }

    let currentPos = 0;
    let result = [];
    let segmentIndex = 0;
    let currentSegmentText = segments[0].text;
    let currentSegmentColor = segments[0].color;
    let segmentStart = 0;

    for (let i = 0; i < displayedText.length; i++) {
      if (segmentIndex < segments.length && i >= segmentStart + currentSegmentText.length) {
        segmentIndex++;
        segmentStart = i;
        if (segmentIndex < segments.length) {
          currentSegmentText = segments[segmentIndex].text;
          currentSegmentColor = segments[segmentIndex].color;
        }
      }

      if (i === segmentStart) {
        if (currentPos < i) {
          result.push(<span key={currentPos}>{displayedText.slice(currentPos, i)}</span>);
        }
        currentPos = i;
      }

      if (segmentIndex < segments.length && i === segmentStart + currentSegmentText.length - 1) {
        result.push(
          <span key={currentPos} style={{ color: currentSegmentColor }}>
            {displayedText.slice(currentPos, i + 1)}
          </span>
        );
        currentPos = i + 1;
      }
    }

    if (currentPos < displayedText.length) {
      result.push(<span key={currentPos}>{displayedText.slice(currentPos)}</span>);
    }

    return result;
  };

  return (
    <motion.p
      style={{ 
        fontSize: 'min(3vw, 20px)',
        lineHeight: '1.8',
        marginBottom: '1rem',
        position: 'relative',
        fontFamily: 'inherit',
        textTransform: 'uppercase',
        color: 'white',
        opacity: opacity,
        transition: useFadeIn ? 'opacity 0.3s ease-out' : 'none',
        ...customStyle
      }}
    >
      {segments ? renderTextWithStyles() : displayedText}
      {isAnimating && (
        <motion.span
          animate={{ opacity: [1, 0] }}
          transition={{ repeat: Infinity, duration: 0.7 }}
          style={{ 
            display: 'inline-block',
            width: '2px',
            height: '1.4rem',
            backgroundColor: '#64ffda',
            marginLeft: '2px',
            verticalAlign: 'middle'
          }}
        />
      )}
    </motion.p>
  );
};

// StudioContent component - the actual content without scroll-based positioning
const StudioContent = ({ isVisible }) => {
  const [showLogos, setShowLogos] = useState(false);
  const [showSecondSentence, setShowSecondSentence] = useState(false);
  
  // Trigger second sentence fade-in at 5 seconds and logos at 6.5 seconds
  useEffect(() => {
    if (isVisible) {
      const secondSentenceTimer = setTimeout(() => {
        setShowSecondSentence(true);
      }, 6500); // 5 seconds
      
      const logoTimer = setTimeout(() => {
        setShowLogos(true);
      }, 9000); // 9 seconds
      
      return () => {
        clearTimeout(secondSentenceTimer);
        clearTimeout(logoTimer);
      };
    } else {
      setShowLogos(false);
      setShowSecondSentence(false);
    }
  }, [isVisible]);

  // Split the text into main paragraph and last sentence
  const mainTextSegments = [
    { text: "Glitch NFT Studio is a collective of artists, creative coders ", color: "white" },
    { text: "&", color: "#64ffda" },
    { text: " entrepreneurs with over a decade of experience at the intersection of ", color: "white" },
    { text: "art", color: "#64ffda" },
    { text: " and technology specializing in web", color: "white" },
    { text: "3", color: "#64ffda" },
    { text: " and generative ", color: "white" },
    { text: " AI", color: "#64ffda" },
    { text: ". Our projects include immersive installations, metaverse scenes, generative art", color: "white" },
    { text: "& ", color: "#5FF916" },
    { text: "  AI-driven animations.", color: "white" }
  ];
  const lastSentence = "We collaborate with high profile clients to create unique digital experiences that bridge the gap between technology and art.";

  return (
    <div 
      style={{ 
        padding: '5rem', 
        color: 'white',
        maxWidth: '85vw',
        margin: '0 auto',
        marginLeft: 'calc(50% - 40vw)',
        position: 'relative',
      }}
    >
      <h1 
        style={{ 
          fontSize: 'min(4vw, 5vw)',
          fontWeight: 600,
          textShadow: '0 0 30px rgba(100, 255, 218, 0.5)',
          marginBottom: '20px',
          // marginTop: '4rem',
          marginLeft: '0.5vw',
          textAlign: 'center',
          color: '#64ffda',
          letterSpacing: '1px',
          lineHeight: '1'
        }}
      >
        Glitch Candies Studio
      </h1>
      
      <div>
        <TypewriterText 
          text="Glitch NFT Studio is a collective of artists, creative coders & entrepreneurs with over a decade of experience at the intersection of art and technology specializing in web3 and generative AI. Our projects include immersive installations, metaverse scenes, generative ART & AI-driven animations."
          visible={isVisible}
          delay={0.5}
          segments={mainTextSegments}
          useTypingEffect={true}
          customStyle={{
            fontWeight: 100,
            fontSize: 'min(1.8vw, 2.5vw)',
            fontFamily: 'Arial, sans-serif',
            marginTop: '5rem',
            marginLeft: '50px',
            color: 'white',
            display: 'block',
            marginBottom: '1rem'
          }}
        />
      </div>
      <TypewriterText 
        text={lastSentence}
        visible={showSecondSentence}
        delay={0}
        useTypingEffect={false}
        useFadeIn={true}
        customStyle={{
          marginLeft: '50px',
          fontSize: 'min(1.8vw, 2.5vw)',
          fontFamily: 'Arial, sans-serif',
          marginTop: '3.5rem',
          color: '#a0a0a0'
        }}
      />
      
      {/* Logo row - events where we presented or collaborated */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: '5rem',
        marginTop: '3rem',
        marginLeft: '4vw',
        marginBottom: '2rem',
        opacity: showLogos ? 1 : 0,
        transition: 'opacity 1.5s ease-in-out'
      }}>
        <img src={token2049Logo} alt="Token 2049" style={{ height: '7vw', filter: 'brightness(0.9)', opacity: 0.9 }} />
        <img src={artBaselLogo} alt="Art Basel" style={{ height: '7vw', filter: 'brightness(0.9)', opacity: 0.9 }} />
        <img src={vuseLogo} alt="Vuse" style={{ height: '3.8vw', filter: 'brightness(0.9)', opacity: 0.9 }} />
        <img src={nftNycLogo} alt="NFT NYC" style={{ height: '7vw', filter: 'brightness(0.9)', opacity: 0.9 }} />
        <img src={nfcSummitLogo} alt="NFC Summit" style={{ height: '7vw', filter: 'brightness(0.9)', opacity: 0.9 }} />
      </div>
    </div>
  );
};

// OverlayStudio component - same content as Studio but controlled by button state
const OverlayStudio = ({ isVisible, onClose }) => {
  // Add escape key listener
  useEffect(() => {
    if (!isVisible) return;
    
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ y: '100vh' }}
      animate={{ y: 0 }}
      exit={{ y: '100vh' }}
      transition={{ 
        type: "spring", 
        stiffness: 300, 
        damping: 30,
        duration: 0.6 
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        background: '#000',
        zIndex: 5000, // Above video and section titles but below overlay buttons
        pointerEvents: 'auto'
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '30px',
          right: '30px',
          background: 'transparent',
          border: '2px solid #64ffda',
          color: '#64ffda',
          padding: '10px 20px',
          borderRadius: '5px',
          cursor: 'pointer',
          fontSize: '14px',
          zIndex: 5001
        }}
      >
        ✕ Close (ESC)
      </button>
      
      {/* Studio content */}
      <StudioContent isVisible={true} />
    </motion.div>
  );
};

const OverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10000; /* Much higher z-index to ensure it's above Studio component */
  pointer-events: none;
`;

export function Overlay() {
  const [aboutVisible, setAboutVisible] = useState(false);
  const [studioVisible, setStudioVisible] = useState(false);
  const { navigateToStudio, showSectionTitles, horizontalProgress } = useSectionsStore();
  
  // Debug logging
  // console.log('Overlay render - showSectionTitles:', showSectionTitles);
  // console.log('Overlay render - navigateToStudio function:', typeof navigateToStudio, navigateToStudio);
  
  // Track when showSectionTitles changes
  useEffect(() => {
    console.log('Overlay useEffect - showSectionTitles changed to:', showSectionTitles);
  }, [showSectionTitles]);
  
  // Hide BUILD WITH US button when in Studio section (horizontalProgress >= 0.9)
  const showBuildButton = horizontalProgress < 0.9;
  
  return (
    <OverlayContainer>
      <AnimatedLink 
        // href="https://glitchcandies.art" 
        style={{ position: 'absolute', bottom: 59, left: 115, fontSize: '12px' }}
        icon={false}
      >
        GLITCH 
        <br />
        CANDIES
        <br />
        STUDIO

      </AnimatedLink>
      
      <AnimatedLink 
        href="https://x.com/glitchcandies" 
        className="follow-x-button"
        style={{ position: 'absolute', top: 35, left: 30, fontSize: '13px', backgroundColor: 'rgba(0, 0, 0, 0.3)', padding: '6px 12px', borderRadius: '9px' }}
        icon={true}
      >
        Follow us on X
      </AnimatedLink>
      
      {/* Audio Button - positioned in top right */}
      <div style={{ position: 'absolute', top: 35, right: 30 }}>
        <AudioButton />
      </div>
      
      {/* Only show BUILD WITH US button when not in Studio section */}
      {showBuildButton && (
        <AnimatedLink 
          onClick={() => {
            console.log('BUILD WITH US button clicked in Overlay');
            
            // Show the overlay studio instead of navigating
            setStudioVisible(true);
          }}
          style={{ position: 'absolute', bottom: 60, right: 20, fontSize: '14px', backgroundColor: 'rgba(0, 0, 0, 0.3)', padding: '6px 12px', borderRadius: '9px' }}
          icon={true}
        >
          BUILD WITH US
        </AnimatedLink>
      )}
      
      {/* Project Info Component */}
      <ProjectInfo />
      
      {/* About modal */}
      <About isAboutVisible={aboutVisible} onClose={() => setAboutVisible(false)} />
      
      {/* Overlay Studio */}
      <OverlayStudio 
        isVisible={studioVisible} 
        onClose={() => setStudioVisible(false)} 
      />
      
      {/* Section titles - only render when showSectionTitles is true */}
      {/* {console.log('Conditional render check - showSectionTitles:', showSectionTitles)} */}
      {showSectionTitles && <SectionTitles />}
      {!showSectionTitles && console.log('SectionTitles component NOT rendered')}
      
      {/* GNS Logo */}
      <motion.img 
        src={gnsLogo} 
        alt="GNS Logo" 
        style={{ 
          position: 'absolute', 
          bottom: 50, 
          left: 45, 
          width: 65,
          height: 'auto',
          pointerEvents: 'all'
        }}
        initial={{ scale: 1 }}
        whileHover={{ scale: 1.05, rotate: 0 }}
        whileTap={{ scale: 0.95 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 15
        }}
      />
    </OverlayContainer>
  )
} 